import React, { useContext, useMemo, useState } from 'react';
import {
  InfoCircleOutlined,
  PlusOutlined,
  LogoutOutlined,
  CalculatorOutlined,
  BarChartOutlined,
  AreaChartOutlined,
  HomeOutlined,
  LeftOutlined,
  RightOutlined,
  LoadingOutlined,
  WarningOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { AgentConfig } from '../../../types/agent.types';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { clearActiveAgent, fetchAgentsThunk } from '../../../store/slices/agentSlice';
import { selectAgent } from '../../../store/actions/agentActions';
import { createSessionThunk } from '../../../store/thunks/sessionThunks';
import { AuthUserContext } from '../../../auth/AuthUserContext';
import { AgentInfoPopover } from '../AgentInfoPopover';
import GrundfosLogo from '../../../assets/images/Grundfos_Logo-Symbol_White_SCREEN.svg';
import {
  AgentSidebar as AgentSidebar__Root,
  AgentSidebar__Header,
  AgentSidebar__Logo,
  AgentSidebar__Title,
  AgentSidebar__HeaderActions,
  AgentSidebar__CollapseBtn,
  AgentSidebar__HomeTile,
  AgentSidebar__HomeTileIcon,
  AgentSidebar__HomeTileName,
  AgentSidebar__SearchWrap,
  AgentSidebar__Search,
  AgentSidebar__List,
  AgentSidebar__Item,
  AgentSidebar__ItemIcon,
  AgentSidebar__ItemName,
  AgentSidebar__ItemActions,
  AgentSidebar__ItemInfoBtn,
  AgentSidebar__NewChatBtn,
  AgentSidebar__UserSection,
  AgentSidebar__UserAvatar,
  AgentSidebar__UserInfo,
  AgentSidebar__UserName,
  AgentSidebar__UserEmail,
  AgentSidebar__LogoutBtn,
  AgentSidebar__StateWrap,
  AgentSidebar__StateIcon,
  AgentSidebar__RetryBtn,
  AgentSidebar__ItemImage,
} from './AgentSidebar.styles';

const ICON_MAP: Record<string, React.ReactNode> = {
  calculator: <CalculatorOutlined />,
  'chart-bar': <BarChartOutlined />,
};

function getAgentIcon(iconKey: string): React.ReactNode {
  return ICON_MAP[iconKey] ?? <AreaChartOutlined />;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface PopoverState {
  agentId: string;
  anchorRect: DOMRect;
}

interface AgentSidebarProps {
  collapsed: boolean;
  onToggleCollapse?: () => void;
  onAgentSelect?: () => void;
}

export const AgentSidebar: React.FC<AgentSidebarProps> = ({
  collapsed,
  onToggleCollapse,
  onAgentSelect,
}) => {
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const { userInfo } = useContext(AuthUserContext);
  const activeAgent = useAppSelector((state) => state.agent.activeAgent);
  const agents = useAppSelector((state) => state.agent.agents);
  const agentsLoading = useAppSelector((state) => state.agent.agentsLoading);
  const agentsError = useAppSelector((state) => state.agent.agentsError);

  const [popover, setPopover] = useState<PopoverState | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return agents;
    const q = searchQuery.toLowerCase();
    return agents.filter(
      (agent) =>
        agent.name.toLowerCase().includes(q) ||
        (agent.description && agent.description.toLowerCase().includes(q)) ||
        (agent.category && agent.category.toLowerCase().includes(q)) ||
        (agent.tags && agent.tags.some((tag) => tag.toLowerCase().includes(q))),
    );
  }, [searchQuery, agents]);

  const handleAgentClick = (agent: AgentConfig) => {
    selectAgent(dispatch, agent);
    onAgentSelect?.();
  };

  const handleNewChat = (e: React.MouseEvent, agent: AgentConfig) => {
    e.stopPropagation();
    selectAgent(dispatch, agent);
    dispatch(createSessionThunk());
    onAgentSelect?.();
  };

  const handleInfoClick = (e: React.MouseEvent, agentId: string) => {
    e.stopPropagation();
    if (popover?.agentId === agentId) {
      setPopover(null);
    } else {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setPopover({ agentId, anchorRect: rect });
    }
  };

  const handleGoHome = () => {
    dispatch(clearActiveAgent());
    onAgentSelect?.();
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const handleRetryFetch = () => {
    dispatch(fetchAgentsThunk());
  };

  const popoverAgent = popover
    ? agents.find((a) => a.id === popover.agentId)
    : null;

  return (
    <AgentSidebar__Root $collapsed={collapsed}>
      <AgentSidebar__Header $collapsed={collapsed}>
        <AgentSidebar__Logo src={GrundfosLogo} alt="Grundfos" />
        <AgentSidebar__Title $collapsed={collapsed}>AI Agents</AgentSidebar__Title>
        {!collapsed && onToggleCollapse && (
          <AgentSidebar__HeaderActions>
            <AgentSidebar__CollapseBtn
              onClick={onToggleCollapse}
              title="Collapse sidebar"
            >
              <LeftOutlined />
            </AgentSidebar__CollapseBtn>
          </AgentSidebar__HeaderActions>
        )}
        {collapsed && onToggleCollapse && (
          <AgentSidebar__CollapseBtn
            onClick={onToggleCollapse}
            title="Expand sidebar"
          >
            <RightOutlined />
          </AgentSidebar__CollapseBtn>
        )}
      </AgentSidebar__Header>

      <AgentSidebar__HomeTile
        $active={!activeAgent}
        $collapsed={collapsed}
        onClick={handleGoHome}
        title={collapsed ? 'Home' : undefined}
      >
        <AgentSidebar__HomeTileIcon $active={!activeAgent}>
          <HomeOutlined />
        </AgentSidebar__HomeTileIcon>
        <AgentSidebar__HomeTileName $active={!activeAgent} $collapsed={collapsed}>
          Home
        </AgentSidebar__HomeTileName>
      </AgentSidebar__HomeTile>

      <AgentSidebar__SearchWrap $collapsed={collapsed}>
        <AgentSidebar__Search
          type="text"
          placeholder="Search agents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </AgentSidebar__SearchWrap>

      <AgentSidebar__List $collapsed={collapsed}>
        {agentsLoading && filteredAgents.length === 0 && (
          <AgentSidebar__StateWrap>
            <AgentSidebar__StateIcon><LoadingOutlined /></AgentSidebar__StateIcon>
            {!collapsed && <div>Loading agents...</div>}
          </AgentSidebar__StateWrap>
        )}

        {agentsError && !agentsLoading && (
          <AgentSidebar__StateWrap $color="error">
            <AgentSidebar__StateIcon><WarningOutlined /></AgentSidebar__StateIcon>
            {!collapsed && (
              <>
                <div>Failed to load agents</div>
                <AgentSidebar__RetryBtn onClick={handleRetryFetch}>
                  <ReloadOutlined /> Retry
                </AgentSidebar__RetryBtn>
              </>
            )}
          </AgentSidebar__StateWrap>
        )}

        {!agentsLoading && !agentsError && filteredAgents.length === 0 && (
          <AgentSidebar__StateWrap>
            {!collapsed && <div>No agents available</div>}
          </AgentSidebar__StateWrap>
        )}

        {filteredAgents.map((agent) => {
          const isActive = activeAgent?.id === agent.id;
          return (
            <AgentSidebar__Item
              key={agent.id}
              $active={isActive}
              $collapsed={collapsed}
              onClick={() => handleAgentClick(agent)}
              title={collapsed ? agent.name : undefined}
            >
              <AgentSidebar__ItemIcon $active={isActive}>
                {agent.imageBase64 ? (
                  <AgentSidebar__ItemImage
                    src={`data:image/png;base64,${agent.imageBase64}`}
                    alt={agent.name}
                  />
                ) : (
                  getAgentIcon(agent.icon)
                )}
              </AgentSidebar__ItemIcon>
              <AgentSidebar__ItemName $active={isActive} $collapsed={collapsed}>
                {agent.name}
              </AgentSidebar__ItemName>
              <AgentSidebar__ItemActions $collapsed={collapsed}>
                <AgentSidebar__ItemInfoBtn
                  onClick={(e) => handleInfoClick(e, agent.id)}
                  title="Agent info"
                >
                  <InfoCircleOutlined />
                </AgentSidebar__ItemInfoBtn>
                {agent.redirectUrl ? (
                  <AgentSidebar__NewChatBtn
                    style={{ visibility: 'hidden' }}
                    aria-hidden="true"
                    tabIndex={-1}
                  >
                    <PlusOutlined />
                  </AgentSidebar__NewChatBtn>
                ) : (
                  <AgentSidebar__NewChatBtn
                    data-role="new-chat"
                    onClick={(e) => handleNewChat(e, agent)}
                    title="New chat"
                  >
                    <PlusOutlined />
                  </AgentSidebar__NewChatBtn>
                )}
              </AgentSidebar__ItemActions>
            </AgentSidebar__Item>
          );
        })}
        {agentsLoading && filteredAgents.length > 0 && (
          <AgentSidebar__StateWrap style={{ padding: '8px 0', fontSize: '12px' }}>
            <AgentSidebar__StateIcon><LoadingOutlined /></AgentSidebar__StateIcon>
            {!collapsed && <div>Loading more agents...</div>}
          </AgentSidebar__StateWrap>
        )}
      </AgentSidebar__List>

      <AgentSidebar__UserSection $collapsed={collapsed}>
        <AgentSidebar__UserAvatar
          title={collapsed ? userInfo.displayName : undefined}
        >
          {getInitials(userInfo.displayName)}
        </AgentSidebar__UserAvatar>
        <AgentSidebar__UserInfo $collapsed={collapsed}>
          <AgentSidebar__UserName>{userInfo.displayName}</AgentSidebar__UserName>
          <AgentSidebar__UserEmail>{userInfo.email}</AgentSidebar__UserEmail>
        </AgentSidebar__UserInfo>
        <AgentSidebar__LogoutBtn
          $collapsed={collapsed}
          onClick={handleLogout}
          title="Sign out"
        >
          <LogoutOutlined />
        </AgentSidebar__LogoutBtn>
      </AgentSidebar__UserSection>

      {popover && popoverAgent && (
        <AgentInfoPopover
          agent={popoverAgent}
          anchorRect={popover.anchorRect}
          onClose={() => setPopover(null)}
        />
      )}
    </AgentSidebar__Root>
  );
};
